import React, { useContext } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { translateBlockValue } from 'i18n';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import AggStatusCellRenderer from 'routes/BudgetModule/components/BudgetProposal/Resource/ExpenseIncome/components/AggStatusCellRenderer';
import { useTranslation } from 'react-i18next';
import {
    BudgetProposalT,
    SecondaryBudgetAxisElementT,
} from 'routes/BudgetModule/config/types';
import PhaseCellRenderer from 'routes/Admin/Budget/BudgetManagement/BudgetList/CellRenderers/PhaseCellRenderer';
import { isActiveBudgetProposal } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import Loading from 'components/Loading';
import { SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID } from 'routes/BudgetModule/config/constants';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { getDetailColumns } from './gridHelpers';
import { useGetOperationAssociations } from 'routes/BudgetModule/components/Operation360/helpers/hooks';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { useGetPhases } from 'routes/BudgetModule/shared/phases/hook';

export const StyDetailCellRenderer = styled(Box, {
    name: 'StyDetailCellRenderer',
})(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '2.5rem',
    background: theme.palette.lightGrey,
    '&&& .ag-header-viewport, .ag-center-cols-viewport, .ag-row': {
        background: theme.palette.lightGrey,
    },
    '&&& .MuiOutlinedInput-root': {
        background: 'white',
    },
    '& .ag-center-cols-viewport': {
        // otherwise expanding an operation with only 1 BP will display a large empty space
        minHeight: 'unset !important',
    },
    '&&& .ag-header-cell-text': {
        color: theme.palette.darkGrey,
        fontSize: '9px',
    },
    '&&& .tag': {
        padding: '0.4em 0.8em !important',
    },
}));

export default function DetailCellRenderer({
    data,
    inscriptionsByOperation,
    allBudgetProposals,
}: {
    data: SecondaryBudgetAxisElementT;
    inscriptionsByOperation: InscriptionT[];
    allBudgetProposals: BudgetProposalT[];
}): React.ReactElement {
    const { t } = useTranslation();
    const { data: phasesSettings = [] } = useGetPhases();

    const operationSoftwareId = data[SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID];
    const statusesSettings = useContext(StatusSettingsContext);

    const columns = getDetailColumns(
        operationSoftwareId,
        allBudgetProposals,
        statusesSettings,
        phasesSettings,
        t,
    );

    const colDefs: ColDef[] = columns.map((column) => {
        const props = {
            headerName: translateBlockValue(column.field || '', true),
            ...column,
            sortable: true,
            resizable: true,
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
            },
        };

        return { key: column.field, ...props };
    });

    const {
        data: budgetProposalByResource = [],
        isLoading: isBpByResourceLoading,
    } = useGetOperationAssociations(
        operationSoftwareId,
        allBudgetProposals[0].by,
    );

    // From the queried gestionnaires, we find which budget proposal each belongs to
    const dataToDisplay = budgetProposalByResource.filter(
        (el) =>
            !!el.budgetProposal && isActiveBudgetProposal(el.budgetProposal),
    );

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };

    return (
        <StyDetailCellRenderer className="ag-theme-material">
            {isBpByResourceLoading ? (
                <Loading />
            ) : (
                <AgGridReact
                    domLayout="autoHeight"
                    rowData={dataToDisplay}
                    components={{
                        aggStatusCellRenderer: AggStatusCellRenderer,
                        phaseCellRenderer: PhaseCellRenderer,
                    }}
                    columnDefs={colDefs}
                    rowHeight={36}
                    context={inscriptionsByOperation}
                    headerHeight={36}
                    rowStyle={{
                        display: 'flex',
                    }}
                    onGridReady={onGridReady}
                />
            )}
        </StyDetailCellRenderer>
    );
}
